import React from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  Button,
  ConfigProvider,
  Empty,
  Tooltip,
  Space,
  Popconfirm,
  theme,
  type TableColumnProps,
  Typography,
} from 'antd';
import {
  UserAddOutlined,
  QuestionCircleOutlined,
  CheckCircleFilled,
  SearchOutlined,
  WarningOutlined,
  DeleteOutlined,
  GoogleOutlined,
} from '@ant-design/icons';

import ButtonWithRole, { SwitchWithRole } from '../../../components/ButtonWithRole';
import Role from '~features/PrerenderUser/Role';
import type Sitemap from '../Sitemap';
import DeviceTypeDropdown, { DeviceTypeLabels } from './DeviceTypeDropdown';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH } from '~components/PointInTime';
import { CustomTooltip } from '~components/Tooltip';
import { useUserRole } from '../../../hooks/usePrerenderUser';
import RevisitIntervalTableDropdown from './RevisitIntervalTableDropdown';
import { getSitemapOrigin, strigifyRevisitInterval } from '../sitemapUtil';

// TOOLTIPS FIRE WARNINGS IN ANTD https://ant.design/components/popover#why-does-the-warning-finddomnode-is-deprecated-sometimes-appear-in-strict-mode
const CustomTrigger = React.forwardRef<HTMLDivElement, React.PropsWithChildren>((props, ref) => (
  <div {...props} ref={ref}>
    {props.children}
  </div>
));
CustomTrigger.displayName = 'CustomTrigger';

const ENV = process.env.REACT_APP_STAGE;

// sticky fires warning logs in RTL while testing, disabling it does not influence the test itself
const STICKY_TABLE = ENV !== 'test' ? { offsetHeader: 0, offsetScroll: 10 } : undefined;

function renderPointInTime(value: string | null) {
  return <PointInTime value={value} />;
}

export type ParsedSitemap = {
  id: Sitemap['adaptiveType'];
  enabled: Sitemap['isEnabled'];
  sitemap_url: string;
  created_at: string;
  interval: number;
  device_type: Sitemap['adaptiveType'];
  last_checked_at: string;
  next_visit: string | null;
  type: string;
  isHealthy: Sitemap['isHealthy'];
  adaptiveType: Sitemap['adaptiveType'];
  health: string;
  domain: string;
};

type SitemapListTableProps = {
  isLoading?: boolean;
  rows?: ParsedSitemap[];
  mobileAdaptive?: boolean;
  onStatusToggle: (sitemap: ParsedSitemap) => void;
  onDeleteClick: (id: ParsedSitemap['id']) => void;
  onDeviceUpdate: (item: ParsedSitemap, adaptiveType: ParsedSitemap['adaptiveType']) => void;
  onRevisitIntervaleUpdate: (item: ParsedSitemap, revisitInterval: ParsedSitemap['interval']) => void;
};

function SitemapListTable({
  rows = [],
  isLoading = false,
  mobileAdaptive,
  onStatusToggle,
  onDeleteClick,
  onDeviceUpdate,
  onRevisitIntervaleUpdate,
}: SitemapListTableProps) {
  const { token } = theme.useToken();
  const userRole = useUserRole();

  const tableColumns: TableColumnProps<ParsedSitemap>[] = React.useMemo(
    () => [
      {
        title: 'Status',
        key: 'status',
        dataIndex: 'enabled',
        width: 90,
        render: (_text, item) => (
          <SwitchWithRole
            disabledFor={[Role.BillingManager, Role.Guest]}
            checked={item.enabled}
            onClick={() => onStatusToggle(item)}
          />
        ),
      },
      {
        title: 'Sitemap URL',
        key: 'sitemapUrl',
        dataIndex: 'sitemapUrl',
        render: (_text, item) => <Link to={`map/${item.id}`}>{item.sitemap_url}</Link>,
      },
      {
        title: 'Created at',
        key: 'createdAt',
        dataIndex: 'created_at',
        width: POINT_IN_TIME_COLUMN_WIDTH,
        render: renderPointInTime,
      },
      {
        title: 'Revisit interval',
        key: 'revisitInterval',
        dataIndex: 'interval',
        width: 150,
        render: (_text, item) => {
          const intervalText = strigifyRevisitInterval(item.interval);

          if ([Role.BillingManager, Role.Guest].includes(userRole))
            return <Typography.Text>{intervalText}</Typography.Text>;

          return (
            <RevisitIntervalTableDropdown
              selectedOptionLabel={intervalText}
              selectedOptionKey={item.interval.toString()}
              onOptionSelected={(option) => onRevisitIntervaleUpdate(item, option.value)}
            />
          );
        },
      },
      {
        title: 'Device',
        key: 'device',
        dataIndex: 'device',
        width: 130,
        render: (_text, item) => {
          if (!mobileAdaptive)
            return (
              <CustomTooltip
                message="To update Device, please enable Mobile Optimized Rendering in Advanced Settings"
                placement="top"
              >
                <Typography.Text>{DeviceTypeLabels.desktop}</Typography.Text>
              </CustomTooltip>
            );

          if ([Role.BillingManager, Role.Guest].includes(userRole))
            return <Typography.Text>{DeviceTypeLabels[item.adaptiveType]}</Typography.Text>;

          return <DeviceTypeDropdown value={item.adaptiveType} onSelect={(value) => onDeviceUpdate(item, value)} />;
        },
      },
      {
        title: 'Last visit',
        key: 'lastVisitedAt',
        dataIndex: 'last_checked_at',
        width: POINT_IN_TIME_COLUMN_WIDTH,
        render: renderPointInTime,
      },
      {
        title: 'Next visit',
        key: 'nextVisit',
        dataIndex: 'next_visit',
        width: POINT_IN_TIME_COLUMN_WIDTH,
        render: renderPointInTime,
      },
      {
        title: 'Origin',
        key: 'origin',
        dataIndex: 'type',
        width: 90,
        render: (_text, item) => {
          const origin = getSitemapOrigin(item.type);
          if (origin === 'gsc')
            return (
              <CustomTooltip message="Imported from Google Search Console" placement="top">
                <GoogleOutlined />
              </CustomTooltip>
            );

          if (origin === 'user')
            return (
              <Tooltip title="Created by you">
                <UserAddOutlined />
              </Tooltip>
            );

          if (origin === 'auto')
            return (
              <Tooltip title="Automaticaly discovered">
                <SearchOutlined />
              </Tooltip>
            );

          return '';
        },
      },
      {
        title: 'Health',
        key: 'health',
        dataIndex: 'health',
        width: 90,
        render: (_text, item) => {
          if (item.isHealthy === null)
            return (
              <Tooltip title="Unknown">
                <QuestionCircleOutlined />
              </Tooltip>
            );
          if (item.isHealthy === true)
            return (
              <Tooltip title="Healthy">
                <CheckCircleFilled style={{ color: token.colorSuccess }} />
              </Tooltip>
            );
          if (item.isHealthy === false)
            return (
              <Tooltip title="Unhealthy">
                <WarningOutlined style={{ color: token.colorError }} />
              </Tooltip>
            );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 160,
        render: (_text, item) => (
          <Space>
            <Link to={`map/${item.id}`}>
              <Button type="link">Details</Button>
            </Link>
            <Popconfirm
              key="delete"
              title="Delete this sitemap?"
              okText="Yes, delete"
              okType="danger"
              cancelText="Cancel"
              placement="left"
              onConfirm={() => onDeleteClick(item.id)}
            >
              <CustomTrigger>
                <ButtonWithRole
                  disabledFor={[Role.BillingManager, Role.Guest]}
                  disabled={isLoading}
                  danger
                  icon={<DeleteOutlined />}
                />
              </CustomTrigger>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [mobileAdaptive, isLoading, userRole]
  );

  return (
    <ConfigProvider
      renderEmpty={() => (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="We haven't discovered sitemaps for your account. We try every 24 hour, please check back later or use the import button."
        />
      )}
    >
      <Table<ParsedSitemap>
        bordered={!!rows.length}
        rowKey="id"
        loading={isLoading}
        columns={tableColumns}
        dataSource={rows}
        pagination={false}
        showHeader={!!rows.length}
        scroll={{ x: 1600 }}
        sticky={STICKY_TABLE}
      />
    </ConfigProvider>
  );
}

export default SitemapListTable;
